import { memo, useEffect, useMemo, useState } from 'react';
import { Flex, Field, Text, Input, Box } from '@chakra-ui/react';
import { Controller } from 'react-hook-form';
import { DatePicker } from '../DatePicker';
import type { FieldProps } from './helpers';

interface EditBirthdayProps extends FieldProps {}

const EditBirthday = ({ user, form }: EditBirthdayProps) => {
  const { t } = useTranslation();
  const {
    formState: { errors },
    control,
  } = form;
  const [value, setValue] = useState<string | undefined>(user?.birthday);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const datePickerWrapperRef = useRef<HTMLDivElement>(null);

  const maxDate = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  }, []);

  const minDate = useMemo(() => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 150);
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  useClickAway(
    (event) => {
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('.react-datepicker__year-dropdown')
      ) {
        return;
      }
      setOpenDatePicker(false);
    },
    [datePickerWrapperRef]
  );

  useEffect(() => {
    if (user?.birthday) {
      setValue(user.birthday);
    } else {
      setValue(undefined);
    }
  }, [user?.birthday]);

  return (
    <Flex
      w="full"
      px="3"
      py="2.5"
      bg="bg.panel"
      rounded="lg"
      alignItems="center"
      justifyContent="space-between"
    >
      <Text fontSize="sm">{t('profile.birthday')}</Text>
      <Field.Root
        invalid={!!errors.birthday}
        flex="1"
        flexDirection="row"
        justifyContent="flex-end"
        zIndex={1}
      >
        <Controller
          control={control}
          name="birthday"
          defaultValue={user?.birthday}
          render={({ field }) => (
            <Box ref={datePickerWrapperRef}>
              <DatePicker
                open={openDatePicker}
                name={field.name}
                tabIndex={-1}
                placeholderText={t('profile.selectBirthday')}
                customInput={<Input size="sm" />}
                dateFormat="yyyy-MM-dd"
                minDate={minDate}
                maxDate={maxDate}
                onInputClick={() => {
                  setOpenDatePicker(true);
                }}
                onChange={(val: Date | null) => {
                  const ms = val?.getTime();
                  setValue(ms?.toString());
                  field.onChange(ms || -1);
                  setOpenDatePicker(false);
                }}
                selected={value ? new Date(+value) : undefined}
                isClearable
                autoComplete="off"
                showYearDropdown
                dateFormatCalendar="MMMM"
                yearDropdownItemNumber={50}
                scrollableYearDropdown
                dropdownMode="scroll"
              />
            </Box>
          )}
        />
      </Field.Root>
    </Flex>
  );
};

const MemoizedEditBirthday = memo(EditBirthday);

export { MemoizedEditBirthday as EditBirthday };
